import { Check, Close, DeleteOutline, EditOutlined } from "@mui/icons-material";
import { IconButton, type IconButtonProps, Tooltip } from "@mui/material";
import { type ReactNode, useState } from "react";

export const ACTIONS_CELL_SX = {
	"& > *": { display: "inline-flex", verticalAlign: "middle" },
	textAlign: "center",
	whiteSpace: "nowrap",
} as const;

export const ActionButton = ({
	title,
	testId,
	color,
	disabled = false,
	forceOpen = false,
	onClick,
	children,
}: {
	title: ReactNode;
	testId?: string;
	color?: IconButtonProps["color"];
	disabled?: boolean;
	forceOpen?: boolean;
	onClick: () => void;
	children: ReactNode;
}) => {
	const [isHovered, setHovered] = useState(false);

	return (
		<Tooltip
			title={title}
			open={(isHovered || forceOpen) && title !== ""}
			onOpen={() => setHovered(title !== "")}
			onClose={() => setHovered(false)}
		>
			<span>
				<IconButton
					size="small"
					data-test-id={testId}
					color={color}
					sx={{ p: 0.25 }}
					disabled={disabled}
					onClick={onClick}
				>
					{children}
				</IconButton>
			</span>
		</Tooltip>
	);
};

type RowActionsProps = {
	isEditing: boolean;
	isLocked?: boolean;
	canEdit?: boolean;
	editTitle: string;
	deleteTitle: string;
	canDelete?: boolean;
	blockedReason?: string | null;
	isReasonShown?: boolean;
	testId?: string;
	onEdit?: () => void;
	onDelete?: () => void;
	onCancel?: () => void;
	onSave?: () => void;
};

export const RowActions = ({
	isEditing,
	isLocked = false,
	canEdit = true,
	editTitle,
	deleteTitle,
	canDelete = true,
	blockedReason = null,
	isReasonShown = false,
	testId,
	onEdit = () => {},
	onDelete = () => {},
	onCancel = () => {},
	onSave = () => {},
}: RowActionsProps) => {
	const testIdOf = (action: string) =>
		testId === undefined ? undefined : `${action}-${testId}`;

	if (isEditing)
		return (
			<>
				<ActionButton
					title="Discard changes (Esc)"
					testId={testIdOf("discard")}
					onClick={onCancel}
				>
					<Close fontSize="small" />
				</ActionButton>
				<ActionButton
					title={blockedReason ?? "Save (Ctrl + Enter)"}
					testId={testIdOf("save")}
					color="primary"
					disabled={blockedReason !== null}
					forceOpen={isReasonShown}
					onClick={onSave}
				>
					<Check fontSize="small" />
				</ActionButton>
			</>
		);

	return (
		<>
			<ActionButton
				title={canEdit ? editTitle : ""}
				testId={testIdOf("edit")}
				disabled={!canEdit}
				onClick={onEdit}
			>
				<EditOutlined fontSize="small" />
			</ActionButton>
			<ActionButton
				title={isLocked ? "" : deleteTitle}
				testId={testIdOf("delete")}
				disabled={isLocked || !canDelete}
				onClick={onDelete}
			>
				<DeleteOutline fontSize="small" />
			</ActionButton>
		</>
	);
};
